import React, { useEffect, useState } from 'react';

// UI
import Modal from 'app/components/MainLayout/components/Modal';
import ModalBody from 'app/components/MainLayout/components/Modal/ModalBody';
import { ModalFooter } from 'app/components/MainLayout/components/Modal/ModalFooter';
import { Divider, NumberField, Text, View } from '@upstox/upstox-ui';

// Store, Services
import { observer } from 'mobx-react';
import { useStore } from 'modules/Stores';
import SearchService from 'modules/Search/store';
import ValidationService from 'modules/Validation';
import FormatService from 'modules/Format';
import LocaleService from 'modules/Localization';
import { useSubscribeScripAndGetValue } from 'utils/hooks';

// Types
import { OrderType } from 'modules/Ordering';
import { PositionBase } from 'modules/TradesPW3/Positions/typings';
import { MARKET_FEEDER_MODES } from 'modules/LiveData/constants';

const MAX_LIMIT_PRICE_VALUE = 9999999.9999;
const MAX_LIMIT_PRICE_VALIDATION_MESSAGE = `This field must be less than ${MAX_LIMIT_PRICE_VALUE}`;

type TClosePositionModal = {
  position: PositionBase;
};

const ClosePositionModal: React.FC<TClosePositionModal> = props => {
  const { position } = props;

  const { toggleModal } = useStore('AppStore');
  const {
    generatePositionDetails,
    preClosePosition,
    surveillanceAlertAccepted,
    clearSurveillanceAlertAccepted,
  } = useStore('PositionsPW3Service');
  const positionDetails = generatePositionDetails(position);

  const {
    loadings: { PLACE_ORDER: placeOrderLoading },
  } = useStore('LoadingStore');

  const [isInit, setInit] = useState(false);
  const [isStockOption, setIsStockOption] = useState(false);
  const [displayName, setDisplayName] = useState('');

  const liveData = useSubscribeScripAndGetValue(
    position?.instrument_key || '',
    MARKET_FEEDER_MODES.LTPC,
    true,
  );
  const ltpExists = liveData?.lastTradedPrice !== undefined;
  const ltp = ltpExists ? liveData?.lastTradedPrice : '-';
  const formattedLtp = ltp ? FormatService.formatIndianPrice(ltp) : ltp;

  useEffect(() => {
    async function getTokenScripData() {
      const scripData = await SearchService.searchByExchangeToken(
        `${position.instrument.e}|${position.instrument.t}` || '',
      );
      if (scripData) {
        const isIndex = scripData?.underlyingExchangeToken?.includes('INDEX');
        const isOption = ValidationService.isOption(scripData?.instrument);
        setIsStockOption(!isIndex && isOption);
        setInit(true);
        setDisplayName(scripData?.displayName);
      }
    }

    getTokenScripData();
  }, [position.instrument]);

  const [limitPrice, setLimitPrice] = useState(0);
  const [orderType, setOrderType] = useState(OrderType.Market);
  const limitPriceError =
    limitPrice > MAX_LIMIT_PRICE_VALUE && MAX_LIMIT_PRICE_VALIDATION_MESSAGE;

  useEffect(() => {
    if (isStockOption) {
      setOrderType(OrderType.Limit);
    }
  }, [isStockOption]);

  const details = (
    <View flexDirection="column">
      <View>
        <Text family="tvFont">{displayName}</Text>
      </View>
      <View>
        <Text family="tvFont">
          {LocaleService.translate('QUANTITY')}: {positionDetails.netQty} @{' '}
          {formattedLtp}
        </Text>
      </View>
      <View padding={['small', 'none']}>
        <Text family="tvFont">
          {LocaleService.translate('CLOSE_POSITION_DISCLAIMER')}
        </Text>
      </View>
    </View>
  );

  const getModalInfo = () => {
    let modalForm = <React.Fragment />;
    if (isStockOption) {
      modalForm = (
        <View padding={['small', 'none', 'none']} flexDirection="column">
          <View padding={['small', 'none']}>
            <Text family="tvFont">
              {LocaleService.translate('STOCK_OPTIONS_NOT_ALLOWED')}
            </Text>
          </View>

          <Text weight="normal" family="tvFont">
            {LocaleService.translate('LIMIT_PRICE')}
          </Text>
          <NumberField
            mode="currency"
            placeholder="₹0"
            error={limitPriceError}
            InputProps={{
              minLength: 0,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                const value = event.target.value
                  ? `${FormatService.unformatIndianNumber(
                      event.target.value.split(' ')[1],
                    )}`
                  : '';

                setLimitPrice(Number(value));
              },
            }}
          />
        </View>
      );
    } else if (position.product === 'MTF' || position.product === 'MTFS') {
      return (
        <React.Fragment>
          <ModalBody>
            <View flexDirection="column" style={{ width: '100%' }}>
              <Text family="tvFont">
                {LocaleService.translate('CANNOT_CLOSE_MTF_POSITION')}
              </Text>
            </View>
          </ModalBody>

          <Divider />

          <ModalFooter
            hideConfirmButton
            onClose={() => toggleModal('closePosition')}
          />
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <ModalBody>
          <View flexDirection="column" style={{ width: '100%' }}>
            {details}
            {modalForm}
          </View>
        </ModalBody>

        <Divider />

        <ModalFooter
          confirmActionTitle="Close position"
          isSubmitting={placeOrderLoading}
          disabledSubmitting={
            isStockOption && (!limitPrice || Boolean(limitPriceError))
          }
          onConfirm={async () => {
            await preClosePosition(
              position,
              {
                orderType,
                limitPrice,
              },
              position.exchangeToken
                ? surveillanceAlertAccepted[position.exchangeToken]
                : undefined,
            );
          }}
          onClose={() => {
            toggleModal('closePosition');
            clearSurveillanceAlertAccepted();
          }}
        />
      </React.Fragment>
    );
  };

  return (
    <Modal
      title={LocaleService.translate('CLOSE_POSITION_TITLE')}
      onClose={() => {
        toggleModal('closePosition');
      }}
    >
      <React.Fragment>{isInit && getModalInfo()}</React.Fragment>
    </Modal>
  );
};

export default observer(ClosePositionModal);
